<route>
{
  "meta": {
    "permission": [
      "invoices.view_invoice"
    ]
  }
}
</route>

<template>
  <v-container fluid>
    <v-row row wrap>
      <v-col cols="12" sm="6" md="3">
        <i-date-picker
          v-model="start"
          :label="$tc('start_date')"
          color="primary"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <i-date-picker
          v-model="end"
          :label="$t('end_date')"
          color="primary"
          :min="start"
        />
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <ValidationProvider
          vid="invoice_number"
          :name="$t('number')"
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="form.invoice_number"
            outlined
            :error-messages="errors[0]"
            :label="$t('number')"
            class="secondary--text"
          ></v-text-field>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <ValidationProvider
          vid="customer"
          :name="$tc('customer', 1)"
          v-slot="{ errors }"
        >
          <v-autocomplete
            v-model="form.customer"
            :items="customersList"
            outlined
            :error-messages="errors[0]"
            :label="$tc('customer', 1)"
            class="secondary--text"
            item-text="name"
            item-value="pk"
            clearable
            :menu-props="{ maxHeight: '400' }"
          ></v-autocomplete>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <ValidationProvider
          vid="status"
          :name="$t('status')"
          v-slot="{ errors }"
        >
          <v-select
            v-model="form.status"
            :items="statusList"
            outlined
            :error-messages="errors[0]"
            :label="$t('status')"
            class="secondary--text"
            item-text="text"
            item-value="value"
            clearable
            :menu-props="{ maxHeight: '400' }"
          ></v-select>
        </ValidationProvider>
      </v-col>
    </v-row>
    <i-table
      :title="$t('invoicesReport')"
      :headers="headers"
      app="invoices.invoice"
      :opt="{
        company_id: this.company ? this.company.pk : null,
        date_from: this.start,
        date_to: this.end,
        invoice_number: this.form.invoice_number,
        status: this.form.status,
        customer: this.form.customer
      }"
      api="invoice"
      dontCreate
      dontRemove
      dontEdit
      dontSearch
    >
      <template v-slot:item.status="{ item }">
        <span class="text-capitalize">
          {{
            item.status
              ? item.status === 'paid'
                ? $tc('paid', 2)
                : $te(item.status)
                ? $t(item.status)
                : item.status
              : ''
          }}
        </span>
      </template>
      <template v-slot:item.date="{ item }">
        <span v-text="item.date != null ? dateFormat(item.date) : ''"></span>
      </template>
      <template v-slot:item.tax="{ item }">
        <i-money :value="item.tax" mode="text"></i-money>
      </template>
      <template v-slot:item.subtotal="{ item }">
        <i-money :value="item.subtotal" mode="text"></i-money>
      </template>
      <template v-slot:item.balance="{ item }">
        <i-money :value="item.balance" mode="text"></i-money>
      </template>
      <template v-slot:item.total="{ item }">
        <i-money :value="item.total" mode="text"></i-money>
      </template>
      <template v-slot:extraToolbar>
        <v-divider class="mx-4" inset vertical></v-divider>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-excel"
          :title="$t('download', { value: $tc('invoice', 2) })"
          color="text-dark"
          @click="getInvoicesExcel()"
        >
        </i-btn>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-pdf"
          :title="$t('download', { value: $tc('invoice', 2) })"
          color="text-dark"
          @click="getInvoicesPDF()"
        >
        </i-btn>
      </template>
    </i-table>
  </v-container>
</template>
<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      customers: [],
      start: '',
      end: '',
      form: {
        invoice_number: '',
        date_from: '',
        date_to: '',
        status: '',
        customer: ''
      }
    }
  },
  async mounted() {
    if (this.company && this.company.pk) {
      await this.getCustomers()
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('number'),
          value: 'invoice_number'
        },
        {
          text: this.$t('order_name'),
          value: 'extraInformation.orderName'
        },
        {
          text: this.$tc('customer', 1),
          value: 'extraInformation.customer'
        },
        {
          text: this.$t('status'),
          value: 'status'
        },
        {
          text: this.$t('date'),
          value: 'date'
        },
        {
          text: this.$t('tax'),
          value: 'tax',
          align: 'right'
        },
        {
          text: this.$tc('subtotal', 1),
          value: 'subtotal',
          align: 'right'
        },
        {
          text: this.$t('balance'),
          value: 'balance',
          align: 'right'
        },
        {
          text: this.$t('total'),
          value: 'total',
          align: 'right'
        }
      ]
    },
    statusList() {
      return [
        { text: this.$tc('all', 1), value: '' },
        { text: this.$t('draft'), value: 'draft' },
        { text: this.$tc('paid', 2), value: 'paid' },
        { text: this.$t('canceled'), value: 'canceled' }
      ]
    },
    customersList() {
      const list = (this.customers || []).map((c) => ({
        pk: c.pk || c.id,
        name:
          c.company_name ||
          `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
          c.email
      }))
      return [{ pk: '', name: this.$tc('all', 1) }, ...list]
    },
    ...mapGetters({
      company: 'company/getCompanyData'
    })
  },
  methods: {
    async getCustomers() {
      try {
        const { data } = await this.$api.customer.list({
          opt: {
            params: {
              active: true,
              company_id: this.company ? this.company.pk : null
            }
          }
        })
        this.customers = data.results || data || []
      } catch (e) {
        this.customers = []
      }
    },
    getInvoicesExcel() {
      this.downloadFile({
        api: 'invoice',
        method: 'excel',
        filename: 'Invoices',
        action: 'download',
        ext: 'xlsx',
        opt: {
          pk: this.company.pk,
          params:
            '?status=' +
            this.form.status +
            '&invoice_number=' +
            this.form.invoice_number +
            '&customer=' +
            this.form.customer +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end
        }
      })
    },
    getInvoicesPDF() {
      this.downloadFile({
        api: 'invoice',
        method: 'pdf',
        filename: 'Invoices',
        action: 'download',
        ext: 'pdf',
        opt: {
          pk: this.company.pk,
          params:
            '?status=' +
            this.form.status +
            '&invoice_number=' +
            this.form.invoice_number +
            '&customer=' +
            this.form.customer +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end
        }
      })
    }
  },
  watch: {
    company: {
      handler(val) {
        if (val && val.pk) {
          this.getCustomers()
        }
      }
    },
    start: {
      handler(val) {
        if (val) {
          this.form.date_from = this.dateFormat(val)
        }
      }
    },
    end: {
      handler(val) {
        if (val != null && val != '') {
          this.form.date_to = this.dateFormat(val)
        }
      }
    }
  }
}
</script>
